import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Points,
  ShaderMaterial,
} from 'three';
import { Layer } from './Layer.js';

/**
 * "Desires are already memories."
 *
 * Every shell, telescope and violin the traveller wants long enough gives up
 * a mote. The mote leaves the thing, climbs the well, and stays — drifting
 * in toward the axis of the stair and turning slowly around it, so that by
 * the foot of the descent the whole shaft above is threaded with the things
 * that were wanted on the way down.
 *
 * The motes are not a counter. They are the counter's opposite: you cannot
 * read how many there are, only that the air behind you has become crowded.
 */
export class MemoryWake extends Layer {
  static id = 'memory-wake';
  static order = 70;

  async build() {
    const { settings, palette, bus } = this.ctx;
    const capacity = settings.desire.wakeCapacity;
    this.capacity = capacity;
    this.alive = 0;
    this.cursor = 0;

    const origin = new Float32Array(capacity * 3);
    const birth = new Float32Array(capacity);
    const seed = new Float32Array(capacity);
    const hue = new Float32Array(capacity);

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(origin, 3));
    geo.setAttribute('aBirth', new BufferAttribute(birth, 1));
    geo.setAttribute('aSeed', new BufferAttribute(seed, 1));
    geo.setAttribute('aHue', new BufferAttribute(hue, 1));
    geo.setDrawRange(0, 0);
    geo.boundingSphere = null;
    this.geo = geo;
    this.attrs = { origin: geo.attributes.position, birth: geo.attributes.aBirth, seed: geo.attributes.aSeed, hue: geo.attributes.aHue };

    this.material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uScale: { value: 1 },
        uDrift: { value: settings.desire.wakeDrift },
        uCool: { value: palette.get('memory').clone() },
        uWarm: { value: palette.get('desire').clone() },
        uFinale: { value: 0 },
      },
      vertexShader: /* glsl */ `
        attribute float aBirth;
        attribute float aSeed;
        attribute float aHue;
        uniform float uTime, uScale, uDrift, uFinale;
        varying float vHue;
        varying float vLife;
        varying float vSeed;

        void main() {
          vHue = aHue;
          vSeed = aSeed;
          float age = max(uTime - aBirth, 0.0);
          vLife = smoothstep(0.0, 0.85, age);

          vec3 p = position;

          // Climb: a memory rises out of the thing it was taken from.
          float rise = log(1.0 + age * 0.55) * (3.2 + aSeed * 5.0) * uDrift * 3.0;
          p.y += rise;

          // Fall in toward the axis of the stair, then turn around it.
          float radius = length(p.xz);
          float pull = 1.0 - exp(-age * 0.055) * 0.42;
          float target = mix(radius, 4.6 + aSeed * 3.2, 1.0 - pull);
          float ang = atan(p.z, p.x) + age * (0.035 + aSeed * 0.05);
          float rr = mix(radius, target, 0.55);
          p.x = cos(ang) * rr;
          p.z = sin(ang) * rr;

          // A small private wander, so no two keep the same altitude.
          p.x += sin(uTime * 0.21 + aSeed * 44.0) * 0.5;
          p.y += sin(uTime * 0.17 + aSeed * 23.0) * 0.4;
          p.z += cos(uTime * 0.19 + aSeed * 31.0) * 0.5;

          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          float dist = max(-mv.z, 0.5);
          float born = 1.0 + (1.0 - vLife) * 4.0;        // a flash on release
          gl_PointSize = min(
            (95.0 + aSeed * 75.0) * born * (1.0 + uFinale * 1.8) * uScale / dist,
            80.0 * uScale
          );
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uCool, uWarm;
        uniform float uFinale, uTime;
        varying float vHue;
        varying float vLife;
        varying float vSeed;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float r = length(d);
          float core = smoothstep(0.5, 0.0, r);
          float halo = smoothstep(0.5, 0.18, r);
          float a = core * core * 0.75 + halo * 0.25;

          vec3 col = mix(uCool, uWarm, vHue);
          // Newly released motes are white-hot for a moment.
          col = mix(vec3(1.0), col, clamp(vLife, 0.0, 1.0));
          col += uFinale * vec3(0.5, 0.36, 0.22);

          float flicker = 0.8 + 0.2 * sin(uTime * 1.7 + vSeed * 60.0);
          float alpha = a * (0.35 + 0.65 * vLife) * flicker * (0.8 + uFinale * 0.7);
          if (alpha < 0.002) discard;
          gl_FragColor = vec4(col, alpha);
        }
      `,
    });

    const points = new Points(geo, this.material);
    points.frustumCulled = false;
    points.renderOrder = 5;
    this.points = points;
    this.group.add(points);

    this.finale = 0;
    this._clock = 0;

    bus.on('remember', (e) => this.release(e));
    bus.on('arrive', () => {
      this._finaleWanted = 1;
    });
  }

  /** @param {{position: import('three').Vector3, kind: string}} e */
  release(e) {
    const i = this.cursor;
    const i3 = i * 3;
    this.attrs.origin.array[i3] = e.position.x;
    this.attrs.origin.array[i3 + 1] = e.position.y;
    this.attrs.origin.array[i3 + 2] = e.position.z;
    this.attrs.birth.array[i] = this._clock;
    this.attrs.seed.array[i] = Math.random();
    this.attrs.hue.array[i] = e.kind === 'violin' ? 0.9 : e.kind === 'telescope' ? 0.45 : 0.12;

    this.attrs.origin.needsUpdate = true;
    this.attrs.birth.needsUpdate = true;
    this.attrs.seed.needsUpdate = true;
    this.attrs.hue.needsUpdate = true;

    this.cursor = (this.cursor + 1) % this.capacity;
    this.alive = Math.min(this.alive + 1, this.capacity);
    this.geo.setDrawRange(0, this.alive);
  }

  update(dt, state) {
    const { palette } = this.ctx;
    this._clock = state.elapsed;
    const u = this.material.uniforms;
    u.uTime.value = state.elapsed;
    u.uScale.value = state.pixelRatio;
    u.uCool.value.copy(palette.get('memory'));
    u.uWarm.value.copy(palette.get('desire'));

    const want = this._finaleWanted ?? 0;
    this.finale += (want - this.finale) * Math.min(1, dt * 0.55);
    u.uFinale.value = this.finale;
    if (state.t < 0.97) this._finaleWanted = 0;
  }

  stats() {
    return { memories: this.alive, finale: this.finale.toFixed(2) };
  }
}
